import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Clock, ArrowRight, Package, CreditCard, AlertCircle, TrendingUp, Zap, FileText, ExternalLink } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';
import { trackConversion } from '../utils/referral';

const PACKAGES = [
  { id: 'starter', name: 'Starter', monthlyPrice: 199, annualPricePerMonth: 149, words: '15,000', features: ['Up to 15,000 words/month', '2 revisions', 'Digital delivery'] },
  { id: 'professional', name: 'Professional', monthlyPrice: 395, annualPricePerMonth: 296, words: '50,000', features: ['Up to 50,000 words/month', '5 revisions', 'Cover consultation'] },
  { id: 'complete', name: 'Complete', monthlyPrice: 799, annualPricePerMonth: 599, words: 'Unlimited', features: ['Unlimited words/month', 'Unlimited revisions', 'Cover design', 'Marketing guide'] },
];

export default function GhostwriterDashboard() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const status = searchParams.get('status');
  const [paymentResult, setPaymentResult] = useState(null);
  const [loading, setLoading] = useState(null);
  const [polling, setPolling] = useState(false);
  const [usage, setUsage] = useState(null);
  const [projects, setProjects] = useState([]);

  const fetchUsage = useCallback(async () => {
    try {
      const { data } = await api.get('/ghostwriter/usage');
      setUsage(data);
    } catch { /* not logged in or no sub */ }
    try {
      const { data } = await api.get('/ghostwriter/projects');
      setProjects(data);
    } catch { /* no projects */ }
  }, []);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentResult({ status: 'timeout', message: 'Payment verification timed out. Please check your email.' });
      setPolling(false);
      return;
    }
    try {
      const { data } = await api.get(`/ghostwriter/payment/status/${sid}`);
      if (data.payment_status === 'paid') {
        setPaymentResult({ status: 'paid', message: 'Payment successful! Your project is being set up.', data });
        trackConversion(data.amount_total / 100, 'ghostwriter');
        setPolling(false);
        fetchUsage();
        return;
      }
      if (data.status === 'expired') {
        setPaymentResult({ status: 'expired', message: 'Payment session expired. Please try again.' });
        setPolling(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch {
      setPaymentResult({ status: 'error', message: 'Error checking payment. Please contact support.' });
      setPolling(false);
    }
  }, [fetchUsage]);

  useEffect(() => {
    if (sessionId && status === 'success') {
      setPolling(true);
      pollPaymentStatus(sessionId);
    }
  }, [sessionId, status, pollPaymentStatus]);

  const handlePurchase = async (pkgId) => {
    setLoading(pkgId);
    try {
      const { data } = await api.post('/ghostwriter/purchase', {
        package_id: pkgId,
        billing_cycle: 'annual',
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    } finally { setLoading(null); }
  };

  const usagePercent = usage?.word_limit ? Math.min(100, Math.round((usage.words_used / usage.word_limit) * 100)) : 0;
  const isNearLimit = usagePercent >= 80;
  const isAtLimit = usagePercent >= 100;
  const isUnlimited = usage?.has_subscription && usage?.word_limit === null;
  const isTrial = usage?.is_trial;

  return (
    <>
      <SeoHead title="Dashboard — AI Ghostwriter Pro" />

      <section className="pt-28 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <h1 className="font-serif text-3xl md:text-4xl tracking-tight mb-8" data-testid="dashboard-title">
            Your <span className="text-primary">Dashboard</span>
          </h1>

          {/* Welcome Banner for new/trial users */}
          {usage && isTrial && !paymentResult && (
            <div className="mb-8 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-sm p-6 md:p-8" data-testid="welcome-banner">
              <h2 className="font-serif text-xl md:text-2xl mb-2">Welcome to AI Ghostwriter Pro</h2>
              <p className="text-sm text-muted-foreground mb-5">Your 7-day free trial is active with 7,500 words. Here's how to get started:</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                  <p className="text-primary font-semibold text-sm mb-1">1. Tell Us About Your Book</p>
                  <p className="text-xs text-muted-foreground">Complete the onboarding questionnaire to share your vision.</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                  <p className="text-primary font-semibold text-sm mb-1">2. Choose Your Plan</p>
                  <p className="text-xs text-muted-foreground">Upgrade anytime for more words and features. No credit card needed for trial.</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                  <p className="text-primary font-semibold text-sm mb-1">3. Get Reviews</p>
                  <p className="text-xs text-muted-foreground">Join The Authors' Review Exchange for free Amazon reviews.</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/get-started" data-testid="welcome-questionnaire-btn" className="inline-flex items-center gap-2 rounded-sm px-5 py-2.5 bg-primary text-primary-foreground text-xs font-semibold tracking-wide hover:bg-white hover:text-black transition-all">
                  <ArrowRight className="w-3.5 h-3.5" /> Start Questionnaire
                </Link>
                <Link to="/club" data-testid="welcome-club-btn" className="inline-flex items-center gap-2 rounded-sm px-5 py-2.5 border border-white/20 text-foreground text-xs font-semibold tracking-wide hover:border-primary hover:text-primary transition-all">
                  Join Writers Club
                </Link>
              </div>
            </div>
          )}

          {/* Payment Status */}
          {(polling || paymentResult) && (
            <div className={`mb-8 p-6 rounded-sm border ${paymentResult?.status === 'paid' ? 'border-green-500/30 bg-green-500/5' : paymentResult?.status === 'error' || paymentResult?.status === 'expired' ? 'border-red-500/30 bg-red-500/5' : 'border-primary/30 bg-primary/5'}`} data-testid="payment-status">
              <div className="flex items-center gap-3">
                {polling && !paymentResult && <Clock className="w-5 h-5 text-primary animate-spin" />}
                {paymentResult?.status === 'paid' && <CheckCircle className="w-5 h-5 text-green-400" />}
                {(paymentResult?.status === 'error' || paymentResult?.status === 'expired') && <AlertCircle className="w-5 h-5 text-red-400" />}
                <p className="text-foreground font-medium">{polling && !paymentResult ? 'Verifying payment...' : paymentResult?.message}</p>
              </div>
            </div>
          )}

          {/* Word Usage Section */}
          {usage && (usage.has_subscription || isTrial) && (
            <div className="mb-10" data-testid="usage-section">
              <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" /> Monthly Word Usage
              </h2>
              <div className={`bg-white/5 border rounded-sm p-6 ${isTrial ? 'border-amber-500/20' : 'border-white/10'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-muted-foreground">
                      <span className="text-foreground font-semibold">{usage.package_name}</span> — {usage.month}
                    </p>
                    {isTrial && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-sm" data-testid="trial-badge">Trial</span>
                    )}
                  </div>
                  <p className="text-sm font-semibold" data-testid="usage-count">
                    {isUnlimited ? (
                      <span className="text-primary">{usage.words_used.toLocaleString()} words used</span>
                    ) : (
                      <span className={isAtLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : 'text-foreground'}>
                        {usage.words_used.toLocaleString()} / {usage.word_limit.toLocaleString()} words
                      </span>
                    )}
                  </p>
                </div>

                {!isUnlimited && (
                  <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden" data-testid="usage-bar">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-primary'
                      }`}
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                )}

                {isUnlimited && (
                  <div className="mt-2 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary" />
                    <p className="text-sm text-primary font-medium">Unlimited plan — write as much as you need</p>
                  </div>
                )}

                {/* Near Limit Warning */}
                {isNearLimit && !isAtLimit && !isUnlimited && (
                  <div className="mt-4 p-4 rounded-sm border border-amber-500/30 bg-amber-500/5" data-testid="near-limit-warning">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-amber-400 mb-1">Approaching your word limit</p>
                        <p className="text-xs text-muted-foreground">
                          You've used {usagePercent}% of your monthly allowance. Upgrade to get more words and unlock additional features.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* At Limit — Upgrade CTA */}
                {isAtLimit && !isUnlimited && usage.upgrade_to && (
                  <div className="mt-4 p-5 rounded-sm border border-red-500/30 bg-gradient-to-r from-red-500/10 to-primary/10" data-testid="at-limit-upgrade">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-red-400 mb-1">
                            {isTrial ? 'Free trial limit reached' : 'Word limit reached'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {isTrial ? (
                              <>You've used all 7,500 trial words. Subscribe to <span className="text-primary font-semibold">{usage.upgrade_to.name}</span> for {usage.upgrade_to.word_limit ? `${usage.upgrade_to.word_limit.toLocaleString()} words/month` : 'unlimited words'} starting at ${usage.upgrade_to.annual_price_per_month}/mo.</>
                            ) : (
                              <>You've used all {usage.word_limit.toLocaleString()} words this month. Upgrade to <span className="text-primary font-semibold">{usage.upgrade_to.name}</span> for {usage.upgrade_to.word_limit ? `${usage.upgrade_to.word_limit.toLocaleString()} words/month` : ' unlimited words'} starting at ${usage.upgrade_to.annual_price_per_month}/mo.</>
                            )}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handlePurchase(usage.upgrade_to.id)}
                        disabled={loading === usage.upgrade_to.id}
                        data-testid="upgrade-btn"
                        className="shrink-0 rounded-sm px-6 py-2.5 text-sm font-semibold bg-primary text-primary-foreground hover:bg-white hover:text-black transition-all shadow-[0_0_15px_rgba(212,175,55,0.3)] flex items-center gap-2"
                      >
                        {loading === usage.upgrade_to.id ? 'Processing...' : <><Zap className="w-4 h-4" /> {isTrial ? 'Subscribe Now' : 'Upgrade Now'}</>}
                      </button>
                    </div>
                  </div>
                )}

                {/* Near limit upgrade suggestion (softer) */}
                {isNearLimit && !isAtLimit && !isUnlimited && usage.upgrade_to && (
                  <div className="mt-3 flex items-center justify-between" data-testid="soft-upgrade-cta">
                    <p className="text-xs text-muted-foreground">
                      Need more words? <span className="text-primary font-semibold">{usage.upgrade_to.name}</span> gives you 
                      {usage.upgrade_to.word_limit ? ` ${usage.upgrade_to.word_limit.toLocaleString()} words/month` : ' unlimited words'}.
                    </p>
                    <button
                      onClick={() => handlePurchase(usage.upgrade_to.id)}
                      disabled={loading === usage.upgrade_to.id}
                      data-testid="soft-upgrade-btn"
                      className="shrink-0 text-xs text-primary hover:underline font-semibold flex items-center gap-1"
                    >
                      Upgrade <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Purchase — show only if no active subscription and not trial */}
          {usage && !usage.has_subscription && !isTrial && (
            <div className="mb-12">
              <h2 className="font-serif text-xl mb-6 flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Choose a Plan</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {PACKAGES.map(pkg => (
                  <div key={pkg.id} className="bg-white/5 border border-white/10 rounded-sm p-6" data-testid={`dash-pkg-${pkg.id}`}>
                    <h3 className="font-serif text-lg mb-1">{pkg.name}</h3>
                    <p className="font-serif text-2xl text-primary mb-3">From ${pkg.annualPricePerMonth}<span className="text-sm text-muted-foreground">/mo</span></p>
                    <ul className="space-y-1.5 mb-4">
                      {pkg.features.map((f, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <CheckCircle className="w-3 h-3 text-primary flex-shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={loading === pkg.id}
                      data-testid={`dash-purchase-${pkg.id}`}
                      className="w-full rounded-sm py-2.5 text-sm font-semibold border border-white/20 text-foreground hover:border-primary hover:text-primary transition-all flex items-center justify-center gap-1.5"
                    >
                      {loading === pkg.id ? 'Processing...' : <><CreditCard className="w-3.5 h-3.5" /> Subscribe</>}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Subscription Summary */}
          {usage?.has_subscription && !isTrial && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Your Plan</h2>
                <Link to="/pricing" className="text-xs text-primary hover:underline flex items-center gap-1">
                  View all plans <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="bg-white/5 border border-primary/20 rounded-sm p-6 inline-block" data-testid="active-plan-card">
                <p className="font-serif text-lg text-primary">{usage.package_name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {isUnlimited ? 'Unlimited words/month' : `${usage.word_limit?.toLocaleString()} words/month`}
                </p>
              </div>
            </div>
          )}

          {/* Projects List */}
          {projects.length > 0 && (
            <div className="mb-10" data-testid="projects-section">
              <h2 className="font-serif text-xl mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-primary" /> Your Projects</h2>
              <div className="space-y-2">
                {projects.map(p => (
                  <Link key={p.id} to={`/ghostwriter/project/${p.id}`} className="block bg-white/5 border border-white/10 rounded-sm p-4 hover:border-primary/30 transition-colors group" data-testid={`project-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-serif text-sm group-hover:text-primary transition-colors">{p.package_name || 'Project'}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.billing_cycle} &middot; {p.payment_method || 'stripe'} &middot;
                          <span className={p.status === 'paid' ? 'text-green-400' : 'text-amber-400'}> {p.status}</span>
                          &middot; {new Date(p.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Upgrade Cards — shown when subscribed but not on Complete, or on trial */}
          {usage && (usage.has_subscription || isTrial) && usage.upgrade_to && (
            <div className="mb-10" data-testid="upgrade-section">
              <h2 className="font-serif text-xl mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-primary" /> {isTrial ? 'Choose a Plan' : 'Upgrade Your Plan'}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {PACKAGES.filter(p => {
                  if (isTrial) return true;
                  const tierOrder = ['starter', 'professional', 'complete'];
                  return tierOrder.indexOf(p.id) > tierOrder.indexOf(usage.package_id);
                }).map(pkg => (
                  <div key={pkg.id} className="bg-white/5 border border-primary/20 rounded-sm p-6" data-testid={`upgrade-pkg-${pkg.id}`}>
                    <h3 className="font-serif text-lg mb-1">{pkg.name}</h3>
                    <p className="font-serif text-2xl text-primary mb-1">From ${pkg.annualPricePerMonth}<span className="text-sm text-muted-foreground">/mo</span></p>
                    <p className="text-xs text-muted-foreground mb-4">{pkg.words === 'Unlimited' ? 'Unlimited words/month' : `Up to ${pkg.words} words/month`}</p>
                    <ul className="space-y-1.5 mb-4">
                      {pkg.features.map((f, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <CheckCircle className="w-3 h-3 text-primary flex-shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={loading === pkg.id}
                      data-testid={`upgrade-purchase-${pkg.id}`}
                      className="w-full rounded-sm py-2.5 text-sm font-semibold bg-primary text-primary-foreground hover:bg-white hover:text-black transition-all flex items-center justify-center gap-1.5 shadow-[0_0_10px_rgba(212,175,55,0.2)]"
                    >
                      {loading === pkg.id ? 'Processing...' : <><Zap className="w-3.5 h-3.5" /> Upgrade</>}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Subscription Prompt */}
          {usage && !usage.has_subscription && !isTrial && (
            <div className="bg-white/5 border border-white/10 rounded-sm p-12 text-center" data-testid="no-subscription">
              <p className="text-muted-foreground mb-4">No active subscription. Choose a plan to start your ghostwriting project!</p>
              <Link to="/pricing" data-testid="view-pricing-link" className="text-primary hover:underline text-sm inline-flex items-center gap-1">
                View Pricing <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}

          {/* Not logged in — show plans */}
          {!usage && (
            <div className="mb-12">
              <h2 className="font-serif text-xl mb-6 flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Choose a Plan</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {PACKAGES.map(pkg => (
                  <div key={pkg.id} className="bg-white/5 border border-white/10 rounded-sm p-6" data-testid={`dash-pkg-${pkg.id}`}>
                    <h3 className="font-serif text-lg mb-1">{pkg.name}</h3>
                    <p className="font-serif text-2xl text-primary mb-3">From ${pkg.annualPricePerMonth}<span className="text-sm text-muted-foreground">/mo</span></p>
                    <ul className="space-y-1.5 mb-4">
                      {pkg.features.map((f, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                          <CheckCircle className="w-3 h-3 text-primary flex-shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/pricing"
                      data-testid={`dash-view-pricing-${pkg.id}`}
                      className="w-full rounded-sm py-2.5 text-sm font-semibold border border-white/20 text-foreground hover:border-primary hover:text-primary transition-all flex items-center justify-center gap-1.5"
                    >
                      <CreditCard className="w-3.5 h-3.5" /> View Plans
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
